import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { PERIOD_PRESETS, presetRange, type PeriodPreset } from './salesConstants';

interface SalesPeriodPickerProps {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}

/**
 * The period both the journal and the report are looking at.
 *
 * The journal used to filter by this window without showing it: an entry dated
 * outside the current week simply was not in the list, with nothing on screen to
 * say why or how to widen it. Whatever filters the rows has to be visible above
 * them.
 */
export function SalesPeriodPicker({ from, to, onChange }: SalesPeriodPickerProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-2">
      {PERIOD_PRESETS.map((preset: PeriodPreset) => {
        const [presetFrom, presetTo] = presetRange(preset);
        const active = presetFrom === from && presetTo === to;
        return (
          <Button
            key={preset}
            variant={active ? 'default' : 'outline'}
            size="sm"
            aria-pressed={active}
            onClick={() => onChange(presetFrom, presetTo)}
          >
            {t(`sales.period.${preset}`)}
          </Button>
        );
      })}

      <Input
        aria-label={t('sales.stats.from')}
        type="date"
        className="w-auto"
        value={from}
        max={to}
        onChange={(e) => e.target.value && onChange(e.target.value, to)}
      />
      <Input
        aria-label={t('sales.stats.to')}
        type="date"
        className="w-auto"
        value={to}
        min={from}
        onChange={(e) => e.target.value && onChange(from, e.target.value)}
      />
    </div>
  );
}
